import { useState, useCallback, useRef, useEffect } from 'react';
import { addPoints, POINT_SOURCES } from '../services/pointsService';
import pointsManager from '../services/pointsManager';

const WELLNESS_CONSTANTS = {
  MAX_DAILY_EARNING: 40,
  EGGS_PER_CLICK: 1,
  CLICK_DELAY: 150,
  EFFECT_DURATION: 800
};

/**
 * 웰니스 에그 수집 훅 - 하루 최대 50개 제한 (광고 없음)
 */
export const useWellnessEarning = (tokenUserID) => {
  const [state, setState] = useState({
    dailyEarned: 0,
    maxDailyEarning: WELLNESS_CONSTANTS.MAX_DAILY_EARNING,
    isClicking: false,
    eggsPerClick: WELLNESS_CONSTANTS.EGGS_PER_CLICK,
  });

  const clickEffectsRef = useRef([]);

  // 로컬 스토리지 키 생성 (사용자별)
  const getStorageKey = useCallback(() => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return `wellness_earning_${tokenUserID}_${today}`;
  }, [tokenUserID]);

  // 오늘의 수집 현황 로드
  useEffect(() => {
    if (!tokenUserID) return;

    const storageKey = getStorageKey();
    const savedEarning = localStorage.getItem(storageKey);
    
    if (savedEarning) {
      const earned = parseInt(savedEarning, 10) || 0;
      setState(prev => ({ ...prev, dailyEarned: earned }));
    }
  }, [tokenUserID, getStorageKey]);

  // 클릭 이펙트 생성 - 상수 사용
  const createClickEffect = useCallback(() => {
    const effectId = Date.now();
    clickEffectsRef.current.push(effectId);
    setTimeout(() => {
      clickEffectsRef.current = clickEffectsRef.current.filter(id => id !== effectId);
    }, WELLNESS_CONSTANTS.EFFECT_DURATION);
  }, []);

  // 에그 수집 함수 (광고 없음) - 50개 한번에 전송 최적화
  const collectEggs = useCallback(() => {
    if (state.isClicking || state.dailyEarned >= state.maxDailyEarning) return;

    // 🎯 50개 한번에 전송: 남은 포인트를 모두 한번에 처리
    const remainingPoints = state.maxDailyEarning - state.dailyEarned;
    const pointsToAdd = Math.min(remainingPoints, state.eggsPerClick);
    
    // 상태 업데이트
    const newEarned = state.dailyEarned + pointsToAdd;
    setState(prev => ({
      ...prev,
      isClicking: true,
      dailyEarned: newEarned,
    }));

    // 로컬 스토리지에 저장
    const storageKey = getStorageKey();
    localStorage.setItem(storageKey, newEarned.toString());

    createClickEffect();

    // 🚀 포인트 큐에 추가 (50개 한번에 전송)
    addPoints(pointsToAdd, POINT_SOURCES.WELLNESS);

    // 🎯 50개 달성 시 즉시 서버 전송 (Wellness 최적화)
    if (newEarned >= state.maxDailyEarning) {
      setTimeout(async () => {
        try {
          await pointsManager.forceFlush();
        } catch (error) {
          console.error('❌ Wellness 50개 서버 전송 실패:', error);
        }
      }, 500); // 0.5초 후 즉시 전송
    }

    // 클릭 상태 해제 - 상수 사용
    setTimeout(() => {
      setState(prev => ({ ...prev, isClicking: false }));
    }, WELLNESS_CONSTANTS.CLICK_DELAY);
  }, [state.isClicking, state.dailyEarned, state.maxDailyEarning, state.eggsPerClick, createClickEffect, getStorageKey]);

  return {
    ...state,
    canClick: !state.isClicking && state.dailyEarned < state.maxDailyEarning,
    remainingEggs: state.maxDailyEarning - state.dailyEarned,
    isMaxEarned: state.dailyEarned >= state.maxDailyEarning,
    collectEggs,
    clickEffects: clickEffectsRef.current,
    progressPercentage: (state.dailyEarned / state.maxDailyEarning) * 100,
  };
};
